import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  changeFilterThirdPartySalesByItemsGroupBy,
  findThirdPartySalesByItemsRequested,
  reportFilterThirdPartySalesByItemsGroupBySelector,
  reportThirdPartySalesByItemsLoadingSelector,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  reportFilterCountryCodeSelector,
} from '../state';

import {
  AccountingDateRangeFilter,
  CountryFilter,
  PluFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  ReportFiltersProps,
  StoreFilter,
} from './ReportFilters';

import { AppPermissions, ReportThirdPartySalesByItemsGroupBy } from '../types';
import { PermissionsProvider } from './Permissions';
import { constants } from '../helpers';
import { FilterPanel, StatusChip } from './Visual';

const onedriveAction = () => findThirdPartySalesByItemsRequested(true);

const groupByConfig: ReportFiltersProps<ReportThirdPartySalesByItemsGroupBy>['groupByConfig'] = {
  options: {
    countryAcronym: true,
    storeAcronym: false,
    accountingDate: false,
    plu: false,
  },
  action: changeFilterThirdPartySalesByItemsGroupBy,
  selector: reportFilterThirdPartySalesByItemsGroupBySelector,
};

const ThirdPartySalesByItemsSearch = () => {
  const { t } = useTranslation();
  const startDate = useSelector(reportFilterStartAccountingDateSelector);
  const endDate = useSelector(reportFilterEndAccountingDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const applyDisabled = !(startDate.accept && endDate.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const accountingDateRequiredIncomplete = applyDisabled && !(startDate.accept && endDate.accept);
  return (
    <ReportFilters
      loadingSelector={reportThirdPartySalesByItemsLoadingSelector}
      applyAction={findThirdPartySalesByItemsRequested}
      onedriveAction={onedriveAction}
      groupByConfig={groupByConfig}
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.SALES_TOTALS]}>
        <FilterPanel
          subtitleTranslationKey="thirdPartySalesByItems.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={3}>
              <AccountingDateRangeFilter
                spacing={1}
                maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne}
                requiredIncomplete={accountingDateRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <PluFilter />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default ThirdPartySalesByItemsSearch;
